export type LiveShopItem = {
  id: string;
  name: string;
  description: string;
  type: string;
  rarity: string | null;
  price: number;
  regularPrice: number;
  image: string | null;
  layout: string;
  outDate: string | null;
};

export type LiveCosmetic = {
  id: string;
  name: string;
  description: string;
  type: string;
  typeValue: string | null;
  rarity: string | null;
  series: string | null;
  set: string | null;
  image: string | null;
  added: string | null;
  introduction: string | null;
};

export type LiveNewsItem = {
  id: string;
  title: string;
  body: string;
  image: string | null;
  priority: number;
};

export type SeasonInfo = {
  label: string;
  endDate: string;
  timeZone: string;
  precision: 'date';
  endDateStartUtc: string;
  endDateEndUtc: string;
};

export type PublishedArticle = {
  id: number;
  slug: string;
  kind: 'nexus' | 'external';
  locale: 'en' | 'ar';
  title: string;
  summary: string;
  coverImageUrl: string | null;
  sourceName: string | null;
  sourceUrl: string | null;
  publishedAt: string;
  authorName: string;
  href: string | null;
};

type ShopResponse = {
  ok: boolean;
  source: string;
  fetchedAt: string;
  shopDate: string | null;
  vbuckIcon: string | null;
  items: LiveShopItem[];
};

type CosmeticsResponse = {
  ok: boolean;
  source: string;
  fetchedAt: string;
  items: LiveCosmetic[];
};

type NewsResponse = {
  ok: boolean;
  source: string;
  fetchedAt: string;
  feedDate: string | null;
  officialNewsUrl: string;
  items: LiveNewsItem[];
};

type ArticlesResponse = {
  ok: boolean;
  fetchedAt: string;
  items: PublishedArticle[];
};

type SeasonResponse = {
  ok: boolean;
  source: string;
  sourceUrl: string;
  fetchedAt: string;
  season: SeasonInfo;
};

const getJson = async <T>(url: string, signal?: AbortSignal): Promise<T> => {
  const response = await fetch(url, { signal, headers: { accept: 'application/json' } });
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json() as Promise<T>;
};

export const getShop = (language: 'en' | 'ar', signal?: AbortSignal) =>
  getJson<ShopResponse>(`/api/shop?language=${language}`, signal);

export const getCosmetics = (
  language: 'en' | 'ar',
  options: { query?: string; type?: string; limit?: number } = {},
  signal?: AbortSignal
) => {
  const params = new URLSearchParams({ language, limit: String(options.limit ?? 24) });
  if (options.query) params.set('q', options.query);
  if (options.type && options.type !== 'all') params.set('type', options.type);
  return getJson<CosmeticsResponse>(`/api/cosmetics?${params}`, signal);
};

export const getNews = (language: 'en' | 'ar', signal?: AbortSignal) =>
  getJson<NewsResponse>(`/api/news?language=${language}`, signal);


export const getArticles = (language: 'en' | 'ar', signal?: AbortSignal) =>
  getJson<ArticlesResponse>(`/api/articles?language=${language}&limit=12`, signal);


export const getSeason = (signal?: AbortSignal) =>
  getJson<SeasonResponse>('/api/season', signal);
